import { useAuth0 } from '@auth0/auth0-react'

import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { AuthPromptModal, AuthPromptModalMode } from '../components/AuthPromptModal'
import { ClaimFormModal } from '../components/ClaimFormModal'
import { ClaimStatusTypes } from '../models/ClaimStatus.model'
import { canPerformVerifiedAction } from '../utils/emailVerification'

type UseClaimPageModalOptions = {
  organizationType: 'hospital' | 'agency'
  organizationId: number
  organizationName: string
  initialClaimStatus?: ClaimStatusTypes
  onStatusChange?: (newStatus: ClaimStatusTypes) => void
}

export const useClaimPageModal = ({
  organizationType,
  organizationId,
  organizationName,
  initialClaimStatus = ClaimStatusTypes.UNCLAIMED,
  onStatusChange,
}: UseClaimPageModalOptions) => {
  const { isAuthenticated, user } = useAuth0()
  const location = useLocation()
  const [claimStatus, setClaimStatus] = useState<ClaimStatusTypes>(initialClaimStatus)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [authPromptMode, setAuthPromptMode] = useState<AuthPromptModalMode | null>(null)

  useEffect(() => {
    setClaimStatus(initialClaimStatus)
  }, [initialClaimStatus])

  const openClaimModal = () => {
    if (!isAuthenticated) {
      window.location.href = `/login/redirect/${encodeURIComponent(location.pathname)}`
      return
    }
    if (!canPerformVerifiedAction(isAuthenticated, user)) {
      setAuthPromptMode('verify-email')
      return
    }
    setIsModalOpen(true)
  }

  const handleModalClose = () => setIsModalOpen(false)

  const handleClaimSuccess = () => {
    setClaimStatus(ClaimStatusTypes.PENDING)
    setIsModalOpen(false)
    onStatusChange?.(ClaimStatusTypes.PENDING)
  }

  useEffect(() => {
    if (!isModalOpen) return

    document.body.style.overflow = 'hidden'
    const pageRoot = document.getElementById('page-root')
    if (pageRoot) pageRoot.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = ''
      if (pageRoot) pageRoot.style.overflow = 'auto'
    }
  }, [isModalOpen])

  const claimModal = (
    <>
      {authPromptMode && (
        <AuthPromptModal returnTo={location.pathname} mode={authPromptMode} onClose={() => setAuthPromptMode(null)} />
      )}
      {isModalOpen && (
        <ClaimFormModal
          organizationType={organizationType}
          organizationId={organizationId}
          organizationName={organizationName}
          onClose={handleModalClose}
          onSuccess={handleClaimSuccess}
        />
      )}
    </>
  )

  return {
    claimModal,
    claimStatus,
    openClaimModal,
    setClaimStatus,
  }
}
